<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title" tag="h6">
        {{ $t('payroll.add_employee_configuration') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="employeeConfig.employee"
                :rules="rules.employee"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="orangehrm-module-field-row">
                <oxd-text tag="p" class="orangehrm-module-field-label">
                  {{ $t('payroll.deduct_for_unpaid_leave') }}
                </oxd-text>
                <oxd-switch-input
                  v-model="employeeConfig.deductForUnpaidLeave"
                />
              </div>
            </oxd-grid-item>
          </oxd-grid>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="orangehrm-module-field-row">
                <oxd-text tag="p" class="orangehrm-module-field-label">
                  {{ $t('payroll.deduct_for_late_in') }}
                </oxd-text>
                <oxd-switch-input v-model="employeeConfig.deductForLateIn" />
              </div>
            </oxd-grid-item>
          </oxd-grid>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="orangehrm-module-field-row">
                <oxd-text tag="p" class="orangehrm-module-field-label">
                  {{ $t('payroll.deduct_for_early_out') }}
                </oxd-text>
                <oxd-switch-input v-model="employeeConfig.deductForEarlyOut" />
              </div>
            </oxd-grid-item>
          </oxd-grid>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="employeeConfig.basicSalaryBasis"
                type="select"
                :options="basicSalaryBasisOptions"
                :rules="rules.basicSalaryBasis"
                :label="$t('payroll.basic_salary_basis')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="employeeConfig.excludedDeductionRules"
                type="multiselect"
                :options="deductionRuleOptions"
                :label="$t('payroll.excluded_deduction_rules')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-button-row">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="onCancel"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@/core/util/helper/navigation';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import {APIService} from '@/core/util/services/api.service';
import {required, validSelection} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {OxdSwitchInput} from '@ohrm/oxd';

const employeeConfigModel = {
  employee: null,
  deductForUnpaidLeave: false,
  deductForLateIn: false,
  deductForEarlyOut: false,
  basicSalaryBasis: null,
  excludedDeductionRules: [],
};

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
    'employee-autocomplete': EmployeeAutocomplete,
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/employees/config',
    );
    const deductionRuleHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/global-deduction-rules',
    );
    return {
      http,
      deductionRuleHttp,
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      employeeConfig: {...employeeConfigModel},
      deductionRuleOptions: [],
      rules: {
        employee: [required, validSelection],
        basicSalaryBasis: [required, validSelection],
      },
    };
  },
  computed: {
    basicSalaryBasisOptions() {
      return [
        {
          id: 'monthly_minus_unpaid',
          label: this.$t('payroll.basic_salary_basis_monthly'),
        },
        {
          id: 'daily_attended_annual',
          label: this.$t('payroll.basic_salary_basis_daily'),
        },
      ];
    },
  },
  created() {
    this.employeeConfig.basicSalaryBasis = this.basicSalaryBasisOptions[0];
    this.loadDeductionRules();
  },
  methods: {
    loadDeductionRules() {
      this.deductionRuleHttp
        .getAll({limit: 100, offset: 0})
        .then((response) => {
          const rows = response?.data?.data ?? [];
          this.deductionRuleOptions = rows
            .filter((rule) => rule.isActive !== false)
            .map((rule) => ({
              id: rule.id,
              label: rule.deductionName,
            }));
        });
    },
    onCancel() {
      navigate('/payroll/viewPayrollEmployeeConfig');
    },
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          empNumber: this.employeeConfig.employee.id,
          deductForUnpaidLeave: this.employeeConfig.deductForUnpaidLeave,
          deductForLateIn: this.employeeConfig.deductForLateIn,
          deductForEarlyOut: this.employeeConfig.deductForEarlyOut,
          basicSalaryBasis:
            this.employeeConfig.basicSalaryBasis?.id || 'monthly_minus_unpaid',
          excludedGlobalDeductionRuleIds: (
            this.employeeConfig.excludedDeductionRules || []
          ).map((rule) => rule.id),
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .catch((response) => {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: response?.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.orangehrm-module-field-label {
  font-size: 12px;
}

.orangehrm-module-field-row {
  grid-column-start: 1;
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
}

.orangehrm-button-row {
  gap: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  max-width: 120px;
  margin-left: 60px;

  ::v-deep(.oxd-button--medium) {
    width: 100%;
  }

  @include oxd-respond-to('md') {
    margin-left: unset;
    max-width: unset;
    ::v-deep(.oxd-button--medium) {
      width: unset;
    }
  }
}
</style>
